/**
 * Resolves the active workspace name from compute-api.
 *
 * The compute-api derives the workspace from the single NamespaceConfig in
 * its deployed namespace; when `spec.workspace` is empty, the NamespaceConfig
 * name is used instead (mirrors compute-api/internal/handlers/v1/me.go).
 */
import { useQuery } from '@tanstack/react-query';
import workspaceApi, { type WorkspaceInfo } from '@/lib/api/workspace';

export const workspaceInfoQueryKey = ['workspace-info'] as const;

export interface UseWorkspaceInfoResult {
  workspaceName: string;
  isLoading: boolean;
  data: WorkspaceInfo | undefined;
}

export function useWorkspaceInfo(): UseWorkspaceInfoResult {
  const query = useQuery({
    queryKey: workspaceInfoQueryKey,
    queryFn: () => workspaceApi.get(),
    staleTime: Infinity,
  });

  // Prefer the human display name; fall back to the slug, then the CR name.
  const workspaceName = query.data?.displayName || query.data?.workspace || query.data?.name || '';

  return {
    workspaceName,
    isLoading: query.isLoading,
    data: query.data,
  };
}
